import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useVestaView } from "./vestaStore";
import { Bell, Coins, Gift, Globe, Loader2, UserRound, Wrench, LayoutGrid, Scale, FolderOpen, BookOpen, ClipboardList, FileText, Warehouse, KeyRound, Receipt, Ruler, PlugZap, Tag } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VestaInfo from "@/components/VestaInfo";
import { COPPER } from "./VestaAuth";
import { getAs, useVestaMe } from "./VestaViewAs";
import ProfiloDialog from "./VestaProfilo";
import { PremioDialog } from "./VestaPremio";
import ComunicazioniDialog from "./VestaComunicazioni";
import WebProfileDialog, { useWebProfileActive } from "./VestaWebProfile";
import { ListinoDialog, CommesseDialog, PreventiviDialog, MagazzinoDialog } from "./VestaBackoffice";
import { ConsulenzaWide } from "./VestaChiavi";
import { FissInclinatoDialog, FissPianoDialog, ZavorreVestaDialog, ConfigurazioniDialog } from "./VestaTools";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const nf = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 1 });
const dt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const asParams = () => { const a = getAs(); return a ? { as: a.id } : {}; };

const INNER = {
  "ai-vesta": [
    { key: "profilo", title: "Profilo installatore", desc: "La tua azienda, i tuoi dati e le tue competenze.", icon: UserRound },
    { key: "comunicazioni", title: "Comunicazioni Vesta", desc: "Scegli quali avvisi ricevere sui tuoi impianti, su quali canali e quando.", icon: Bell },
    { key: "punti", title: "I miei Punti Vesta", desc: "Saldo, punti guadagnati per ordine e punti usati.", icon: Coins },
    { key: "catalogo", title: "Catalogo Vesta", desc: "Servizi, strumenti ed esperienze con i tuoi Punti Vesta.", icon: Gift },
    { key: "web-profile", title: "Web Profile", desc: "La tua pagina su torri.solar: foto, servizi e realizzazioni.", icon: Globe, cond: "wp" },
  ],
  "chiavi-in-mano": [
    { key: "cfg-consulenza", title: "Consulenza chiavi in mano", desc: "Progetta e proponi l'impianto completo al cliente finale.", icon: KeyRound, to: "/vesta/configuratore?tool=consulenza" },
    { key: "cfg-bolletta", title: "DEM bolletta", desc: "Analizza la bolletta del cliente e mostra il risparmio.", icon: Receipt, tool: "bolletta" },
    { key: "cfg-tetto", title: "Misura il tetto", desc: "Misura la superficie del tetto e posiziona i moduli.", icon: Ruler, tool: "tetto" },
    { key: "cfg-colonnina", title: "Stazione di ricarica", desc: "Configura la wallbox per l'auto elettrica del cliente.", icon: PlugZap, tool: "colonnina" },
    { key: "configurazioni-comm", title: "Le mie configurazioni", desc: "Le consulenze commerciali salvate: riaprile dal configuratore.", icon: FolderOpen },
  ],
  "cassetta-attrezzi": [
    { key: "fiss-inclinato", title: "Fissaggi tetto inclinato", desc: "Staffe, binari, morsetti e giunti dal layout a falde della tua configurazione.", icon: Wrench },
    { key: "fiss-piano", title: "Fissaggi tetto piano", desc: "Presagomate e zavorre dal layout del campo, con abbinamento automatico.", icon: LayoutGrid },
    { key: "zavorre", title: "Pre-dimensionamento zavorre", desc: "Numero minimo di zavorre per PRSG su tetto piano secondo NTC.", icon: Scale },
    { key: "configurazioni-tec", title: "Le mie configurazioni", desc: "I calcoli tecnici salvati: riaprili e scarica i PDF.", icon: FolderOpen },
  ],
  backoffice: [
    { key: "listino", title: "Listino commerciale", desc: "Prezzi di listino Torri per categoria, con PDF scaricabile.", icon: BookOpen },
    { key: "commesse", title: "Commesse", desc: "I tuoi ordini: stato di consegna, articoli e documenti.", icon: ClipboardList },
    { key: "preventivi", title: "Preventivi", desc: "Tutti i preventivi ricevuti da Torri, con dettaglio e PDF.", icon: FileText },
    { key: "magazzino", title: "Magazzino", desc: "Giacenze aggiornate degli articoli a listino e prossimi arrivi.", icon: Warehouse },
    { key: "tariffario", title: "Tariffario Versione Pro", desc: "I prezzi della Versione Pro per fascia di potenza.", icon: Tag, to: "/vesta/tariffario" },
  ],
};
export const hasInner = (key) => !!INNER[key];

const Shell = ({ open, onOpenChange, title, children, testid }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid={testid}>
      <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-xl" style={{ color: COPPER }}>{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

const useLoad = (open, url) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return;
    setD(null); setErr("");
    api.get(url, { params: asParams() }).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Errore di caricamento"));
  }, [open, url]);
  return [d, err];
};

const Rows = ({ rows, cols, testid }) => (
  <table className="w-full text-sm" data-testid={testid}>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} className="border-b border-neutral-900">
          {cols.map((c, j) => <td key={j} className={`py-1.5 ${j === cols.length - 1 ? "text-right font-mono" : "pr-3"} ${c.cls || ""}`}>{c.v(r)}</td>)}
        </tr>
      ))}
      {rows.length === 0 && <tr><td className="py-2 text-xs text-neutral-500">Nessun movimento</td></tr>}
    </tbody>
  </table>
);

function PuntiDialog({ open, onOpenChange }) {
  const [d, err] = useLoad(open, "/vesta/punti");
  useVestaView(open, { scheda: "Punti Vesta: saldo e movimenti" });
  if (d?.rete) return (
    <Shell open={open} onOpenChange={onOpenChange} testid="vesta-punti-dialog" title={<><img src="/vesta-coin-rame.png" alt="" className="h-7 w-7" /> Punti Vesta della rete</>}>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3 text-center">
          {[["Saldo totale rete", d.saldo, "vesta-punti-rete-saldo"], ["Partner con punti", d.partner_con_punti, "vesta-punti-rete-n"]].map(([l, v, id]) => (
            <div key={l} className="rounded-xl border border-neutral-800 p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">{l}</div>
              <div className="font-heading text-2xl font-extrabold" style={{ color: COPPER }} data-testid={id}>{nf(v)}</div>
            </div>
          ))}
        </div>
        <div>
          <div className="mb-1 text-xs font-bold uppercase tracking-widest text-neutral-400">Classifica saldi</div>
          <Rows testid="vesta-punti-classifica" rows={d.classifica} cols={[{ v: (r) => r.name }, { v: (r) => r.city || "", cls: "text-neutral-400" }, { v: (r) => nf(r.saldo) }]} />
        </div>
      </div>
    </Shell>
  );
  return (
    <Shell open={open} onOpenChange={onOpenChange} testid="vesta-punti-dialog" title={<><img src="/vesta-coin-rame.png" alt="" className="h-7 w-7" /> I miei Punti Vesta <VestaInfo /></>}>
      {err ? <p className="text-sm text-red-400">{err}</p> : !d ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3 text-center">
            {[["Saldo", d.saldo, "vesta-punti-saldo"], ["Guadagnati", d.tot_guadagnati, "vesta-punti-guad"], ["Usati", d.tot_usati, "vesta-punti-usati"]].map(([l, v, id]) => (
              <div key={l} className="rounded-xl border border-neutral-800 p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">{l}</div>
                <div className="font-heading text-2xl font-extrabold" style={{ color: COPPER }} data-testid={id}>{nf(v)}</div>
              </div>
            ))}
          </div>
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-neutral-400">Punti guadagnati per ordine</div>
            <Rows testid="vesta-punti-ordini" rows={d.guadagnati} cols={[{ v: (r) => dt(r.data), cls: "text-neutral-400" }, { v: (r) => r.ordine || "Ordine" }, { v: (r) => `+${nf(r.punti)}` }]} />
          </div>
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-neutral-400">Punti usati</div>
            <Rows testid="vesta-punti-spesi" rows={d.usati} cols={[{ v: (r) => dt(r.data), cls: "text-neutral-400" }, { v: (r) => r.descrizione || "—" }, { v: (r) => `−${nf(r.punti)}` }]} />
          </div>
          <p className="text-[11px] text-neutral-500">I punti per singolo ordine sono indicativi: il saldo ufficiale è calcolato per anno, come nel resto del programma Vesta.</p>
        </div>
      )}
    </Shell>
  );
}

function CatalogoDialog({ open, onOpenChange }) {
  const [d, err] = useLoad(open, "/vesta/catalogo");
  const [sel, setSel] = useState(null);
  return (
    <Shell open={open} onOpenChange={onOpenChange} testid="vesta-catalogo-dialog" title={<><Gift className="h-6 w-6" /> Catalogo Vesta</>}>
      {err ? <p className="text-sm text-red-400">{err}</p> : !d ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : (
        <div className="space-y-6">
          {[...d.categorie, { id: "__altro", name: "Altro" }].map((c) => {
            const known = new Set(d.categorie.map((x) => x.id));
            const ps = d.prodotti.filter((p) => (c.id === "__altro" ? !known.has(p.category_id) : p.category_id === c.id));
            if (!ps.length) return null;
            return (
              <div key={c.id}>
                <div className="mb-2 text-xs font-bold uppercase tracking-widest text-neutral-400">{c.name}</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {ps.map((p) => (
                    <button type="button" key={p.id} onClick={() => setSel(p)} className="overflow-hidden rounded-xl border border-neutral-800 text-left transition-colors hover:border-neutral-500" data-testid={`vesta-premio-${p.id}`}>
                      <div className="aspect-video bg-neutral-900">{p.image_url ? <img src={imgSrc(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Gift className="h-8 w-8 text-neutral-600" /></div>}</div>
                      <div className="p-3">
                        <div className="font-heading font-bold">{p.name}</div>
                        {p.description && <p className="mt-1 line-clamp-3 text-xs text-neutral-400">{p.description}</p>}
                        <div className="mt-2 font-mono text-lg font-black" style={{ color: COPPER }}>{p.costo_punti} <span className="text-xs font-semibold text-neutral-400">Punti Vesta</span></div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {d.prodotti.length === 0 && <p className="text-sm text-neutral-400">Il catalogo è in preparazione.</p>}
          <p className="text-[11px] text-neutral-500">Per prenotare dal Catalogo Vesta contatta il tuo referente Torri.</p>
          <PremioDialog p={sel} categoria={d.categorie.find((c) => c.id === sel?.category_id)?.name} imgSrc={imgSrc} onClose={() => setSel(null)} />
        </div>
      )}
    </Shell>
  );
}

const SoonDialog = (s) => function Soon({ open, onOpenChange }) {
  const Icon = s.icon;
  return (
    <Shell open={open} onOpenChange={onOpenChange} testid={`vesta-soon-dialog-${s.key}`} title={<><Icon className="h-6 w-6" /> {s.title}</>}>
      <p className="text-sm text-neutral-400">{s.desc}</p>
      <span className="mt-2 inline-block w-fit rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest" style={{ borderColor: COPPER, color: COPPER }}>In arrivo</span>
    </Shell>
  );
};
export const DIALOGS = { comunicazioni: ComunicazioniDialog, "web-profile": WebProfileDialog, punti: PuntiDialog, catalogo: CatalogoDialog, profilo: ProfiloDialog, listino: ListinoDialog, "fiss-inclinato": FissInclinatoDialog, "fiss-piano": FissPianoDialog, zavorre: ZavorreVestaDialog, "configurazioni-comm": (p) => <ConfigurazioniDialog {...p} only="commerciali" />, "configurazioni-tec": (p) => <ConfigurazioniDialog {...p} only="tecnici" />, commesse: CommesseDialog, preventivi: PreventiviDialog, magazzino: MagazzinoDialog };
Object.values(INNER).flat().forEach((s) => { if (!DIALOGS[s.key]) DIALOGS[s.key] = SoonDialog(s); });

export function InnerSlots({ slotKey, chiavi }) {
  const [open, setOpen] = useState(null);
  const nav = useNavigate();
  const wp = useWebProfileActive(slotKey === "ai-vesta");
  const me = useVestaMe();
  const items = (INNER[slotKey] || []).filter((s) => (s.cond !== "wp" || wp) && (s.cond !== "admin" || me?.is_admin));
  const [sp, setSp] = useSearchParams();
  const want = sp.get("open");
  const mine = items.some((s) => s.key === want);
  useEffect(() => { if (mine) setOpen(want); }, [want, mine, sp]);
  const focus = mine ? { id: sp.get("id"), q: sp.get("q") } : null;
  const change = (k) => (v) => { setOpen(v ? k : null); if (!v && want === k) setSp({}, { replace: true }); };
  return (
    <div className={`grid grid-cols-2 gap-4 sm:gap-6 ${items.length > 2 ? "sm:grid-cols-3" : ""}`} data-testid="vesta-inner-slots">
      {items.map((s) => {
        const Icon = s.icon;
        const Dlg = DIALOGS[s.key];
        if (s.key === "cfg-consulenza") return <ConsulenzaWide key={s.key} s={s} d={chiavi} />;
        return (
          <div key={s.key}>
            <button type="button" onClick={() => (s.to && !s.tool ? nav(s.to) : setOpen(s.key))} data-testid={`vesta-inner-${s.key}`}
              className="group relative flex aspect-square w-full flex-col justify-between overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-950 p-5 text-left transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-[#C17F59] hover:shadow-[0_0_32px_-6px_rgba(193,127,89,.55)] sm:p-7">
              <Icon className="h-8 w-8 sm:h-10 sm:w-10" style={{ color: COPPER }} strokeWidth={1.6} />
              <div>
                <div className="font-heading text-base font-extrabold leading-tight text-white sm:text-xl">{s.title}</div>
                <p className="mt-1.5 hidden text-xs text-neutral-400 sm:block">{s.desc}</p>
              </div>
            </button>
            {s.tool ? <ConfigurazioniDialog tool={s.tool} only="commerciali" open={open === s.key} onOpenChange={change(s.key)} focus={want === s.key ? focus : null} />
              : !s.to && <Dlg open={open === s.key} onOpenChange={change(s.key)} focus={want === s.key ? focus : null} />}
          </div>
        );
      })}
    </div>
  );
}
